import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import linkifyText from '@adsk/bim360-matrix-react-helpers/es/linkifyText';
import escapeHtml from '@adsk/bim360-matrix-react-helpers/es/escapeHtml';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import editIcon from '@adsk/bim360-matrix-react-assets/edit-icon.svg';
import alloyPencilIcon from '@adsk/bim360-matrix-react-assets/alloy-pencil.svg';
import Loader from '@adsk/bim360-matrix-react-loader';
import { Themes, ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

import './_Editable.scss';

const editIconsMap = {
  [Themes.MATRIX]: editIcon,
  [Themes.ALLOY]: alloyPencilIcon
};

/**
 * Editable
 */
export default class Editable extends Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);
    this.state = {
      inEdit: (props.inEdit || props.startInEdit) && !props.readOnly
    };

    this.setIsEdit = this.setIsEdit.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.inEdit !== nextProps.inEdit) {
      const newEditMode = nextProps.inEdit && !nextProps.readOnly;

      if (newEditMode !== this.state.inEdit) {
        this.setState({ inEdit: newEditMode });
      }
    }
  }

  /**
   * toggle 'IsEdit' mode
   */
  setIsEdit(state) {
    const newState = state || !this.props.valid;
    if (this.props.onEditModeChanged) {
      this.props.onEditModeChanged(newState);
    }
    if (!('inEdit' in this.props)) {
      this.setState({ inEdit: newState });
    }
  }

  renderEditable() {
    /* eslint-disable no-unused-vars */
    const {
      className,
      value,
      placeholder,
      style,
      linkify,
      readOnly,
      multiline,
      isLoading,
      disabled,
      startInEdit,
      valid,
      showEditableIcon,
      ...rest
    } = this.props;
    const editableTextElementName = 'Editable__text';
    const customTextProps = {};
    if (linkify) {
      customTextProps.dangerouslySetInnerHTML = {
        __html: linkifyText(escapeHtml(value || placeholder))
      };
    } else {
      customTextProps.children = value || placeholder;
    }

    customTextProps['data-testid'] = editableTextElementName;
    const onClick = readOnly || isLoading ? noop : () => this.setIsEdit(true);

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <button
            {...rest}
            className={classNames('Editable', `Editable--${theme}`, className, {
              'Editable--read-only': readOnly,
              'Editable--disabled': disabled,
              'Editable--placeholder': !value
            })}
            style={style}
            onClick={onClick}
            type="button"
          >
            <div className="Editable__text-container">
              <div
                className={classNames(editableTextElementName, {
                  'Editable__text--placeholder': !value,
                  'Editable__text--multiline': multiline,
                  'Editable__text--single-line': !multiline
                })}
                {...customTextProps}
              />
            </div>
            <div className="Editable__rightside-wrapper">
              {isLoading && <Loader className="Editable__loader" />}
              {!isLoading && !readOnly && showEditableIcon && (
                <SvgIcon className="Editable__svg">{editIconsMap[theme]}</SvgIcon>
              )}
            </div>
          </button>
        )}
      </ThemeConsumer>
    );
  }
  /**
   * render
   */
  render() {
    const { children, valid } = this.props;
    const { inEdit } = this.state;

    return inEdit || !valid ? children(() => this.setIsEdit(false)) : this.renderEditable();
  }
}

Editable.defaultProps = {
  startInEdit: false,
  showEditableIcon: true,
  multiline: true,
  isLoading: false,
  valid: true
};

Editable.propTypes = {
  /**
   * The edit mode component that will be replaced by the text. This prop is required if readOnly === false
   */
  children: PropTypes.func,
  /**
   * The value that will get rendered when the component will not be in Edit mode.
   * Value as component won't work when linkify=true.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * A placeholder text to render when value is empty
   */
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Whether component should render in readOnly state.
   */
  readOnly: PropTypes.bool,
  /**
   * Whether component should render in disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * A custom className for the container element of the component.
   */
  className: PropTypes.string,
  /**
   * A custom style for the container element of the component.
   */
  style: PropTypes.object,
  /**
   * An optional prop to control the edit state of the component. Makes the component a controlled one.
   * Once this prop is passed the user is fully responsible for managing component's `inEdit` state.
   * If `readOnly` is set to true, this prop will be ignored.
   */
  inEdit: PropTypes.bool,
  /**
   * Indicates if the components starts off in Edit mode
   */
  startInEdit: PropTypes.bool,
  /**
   * Whether to convert url and email strings to anchors. default false
   */
  linkify: PropTypes.bool,
  /**
   * Whether or not to show the editable icon altogether.
   */
  showEditableIcon: PropTypes.bool,
  /**
   * Whether to support multilines. If false, it will truncate the text in a single line
   */
  multiline: PropTypes.bool,
  /**
   * Show a loading indicator instead of the edit icon
   */
  isLoading: PropTypes.bool,
  /**
   * A callback triggered when the component's edit mode changed.
   */
  onEditModeChanged: PropTypes.func,
  /**
   * Flag indicating whether or not the input is in a valid or invalid state. In case of invalid state - render in edit mode
   */
  valid: PropTypes.bool
};
